import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Pencil, Plus, Tags, X } from "lucide-react";
import { useToastPush } from "../../../components/ui/Toast";
import {
  ALL_BLOG_TAGS,
  CREATE_BLOG_TAG,
  UPDATE_BLOG_TAG,
  DELETE_BLOG_TAG,
  type BlogTagData,
} from "./blogTagQueries";

interface Props {
  /** Slug of the tag currently filtering the post list, or null. */
  selectedSlug: string | null;
  /** Select a tag (slug) to filter by, or null to clear the filter. */
  onSelect: (slug: string | null) => void;
  /** Called after rename/delete so the post list can refresh its tag chips. */
  onTagsMutated: () => void;
}

interface QueryData {
  allBlogTags: BlogTagData[];
}

/**
 * Right-rail mini manager for the blog tag vocabulary: lists every tag
 * with its post count, adds/renames/deletes tags, and drives the
 * single-select tag filter on the post list. Visual language follows
 * the editor's right-rail panels (StatusPanel et al).
 */
export function TagManagerPanel({ selectedSlug, onSelect, onTagsMutated }: Props) {
  const toast = useToastPush();
  // cache-and-network: tags can be created/attached from the post editor,
  // so a mount-time cache hit must still revalidate — cache-first would pin
  // the rail to whatever was fetched on the first visit.
  const { data, loading, refetch } = useQuery<QueryData>(ALL_BLOG_TAGS, {
    fetchPolicy: "cache-and-network",
  });
  const [createTag, { loading: creating }] = useMutation(CREATE_BLOG_TAG);
  const [updateTag, { loading: renaming }] = useMutation(UPDATE_BLOG_TAG);
  const [deleteTag] = useMutation(DELETE_BLOG_TAG);

  const [newName, setNewName] = useState("");
  const [renameId, setRenameId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");

  const tags = data?.allBlogTags ?? [];

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    const name = newName.trim();
    if (!name || creating) return;
    try {
      await createTag({ variables: { name } });
      setNewName("");
      await refetch();
    } catch (err) {
      toast({
        kind: "error",
        title: "Couldn't add tag",
        description: (err as Error).message,
      });
    }
  }

  function startRename(tag: BlogTagData) {
    setRenameId(tag.id);
    setRenameValue(tag.name);
  }

  async function commitRename(tag: BlogTagData) {
    const name = renameValue.trim();
    if (!name || renaming) return;
    if (name === tag.name) {
      setRenameId(null);
      return;
    }
    try {
      const result = await updateTag({ variables: { id: tag.id, name } });
      setRenameId(null);
      // Keep the URL filter pointing at the tag under its new slug.
      const newSlug: string | undefined = result.data?.updateBlogTag?.slug;
      if (selectedSlug === tag.slug && newSlug && newSlug !== tag.slug) {
        onSelect(newSlug);
      }
      await refetch();
      onTagsMutated();
    } catch (err) {
      toast({
        kind: "error",
        title: "Couldn't rename tag",
        description: (err as Error).message,
      });
    }
  }

  async function handleDelete(tag: BlogTagData) {
    const noun = tag.postCount === 1 ? "post" : "posts";
    const ok = window.confirm(
      `Delete "${tag.name}"? It will be removed from ${tag.postCount} ${noun}.`,
    );
    if (!ok) return;
    try {
      await deleteTag({ variables: { id: tag.id } });
      if (selectedSlug === tag.slug) onSelect(null);
      await refetch();
      onTagsMutated();
    } catch (err) {
      toast({
        kind: "error",
        title: "Couldn't delete tag",
        description: (err as Error).message,
      });
    }
  }

  return (
    <div className="shadow-card overflow-hidden rounded-lg bg-paper-hi">
      <div className="hair-b border-b px-4 py-3">
        <h3 className="text-[13px] font-semibold">Tags</h3>
        <p className="mt-0.5 text-[11.5px] text-ink-4">
          Select a tag to filter posts.
        </p>
      </div>

      <form onSubmit={handleAdd} className="hair-b border-b px-3 py-2.5">
        <div className="flex items-center gap-1.5">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New tag…"
            aria-label="New tag name"
            className="hair w-full rounded-md border bg-canvas/50 px-2.5 py-1.5 text-[12.5px] outline-none transition focus:border-tide/40"
          />
          <button
            type="submit"
            disabled={!newName.trim() || creating}
            aria-label="Add tag"
            className="hair flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-md border text-ink-3 transition hover:bg-canvas-2/60 hover:text-ink disabled:opacity-40"
          >
            <Plus size={14} />
          </button>
        </div>
      </form>

      {loading && tags.length === 0 ? (
        <div className="space-y-2 p-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="pulse-soft h-7 rounded-md bg-canvas-2" />
          ))}
        </div>
      ) : tags.length === 0 ? (
        <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
          <Tags size={22} strokeWidth={1.5} className="text-ink-4" />
          <p className="text-[12px] text-ink-4">
            No tags yet. Add one above, or tag a post in the editor.
          </p>
        </div>
      ) : (
        <div className="p-2">
          <button
            type="button"
            onClick={() => onSelect(null)}
            className={`flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-left text-[12.5px] transition ${
              selectedSlug === null
                ? "bg-tide-ss/70 font-medium text-tide"
                : "text-ink-2 hover:bg-canvas-2/60"
            }`}
          >
            All posts
          </button>

          {tags.map((tag) =>
            renameId === tag.id ? (
              <form
                key={tag.id}
                onSubmit={(e) => {
                  e.preventDefault();
                  void commitRename(tag);
                }}
                className="px-1 py-0.5"
              >
                <input
                  autoFocus
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") setRenameId(null);
                  }}
                  onBlur={() => setRenameId(null)}
                  aria-label={`Rename tag ${tag.name}`}
                  className="hair w-full rounded-md border bg-canvas/50 px-2 py-1 text-[12.5px] outline-none focus:border-tide/40"
                />
              </form>
            ) : (
              <div key={tag.id} className="group flex items-center gap-0.5">
                <button
                  type="button"
                  onClick={() =>
                    onSelect(selectedSlug === tag.slug ? null : tag.slug)
                  }
                  className={`flex min-w-0 flex-1 items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-left text-[12.5px] transition ${
                    selectedSlug === tag.slug
                      ? "bg-tide-ss/70 font-medium text-tide"
                      : "text-ink-2 hover:bg-canvas-2/60"
                  }`}
                >
                  <span className="truncate">{tag.name}</span>
                  <span
                    className={`num flex-shrink-0 text-[11px] ${
                      selectedSlug === tag.slug ? "text-tide" : "text-ink-4"
                    }`}
                  >
                    {tag.postCount}
                  </span>
                </button>
                <div className="flex flex-shrink-0 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                  <button
                    type="button"
                    onClick={() => startRename(tag)}
                    aria-label={`Rename ${tag.name}`}
                    className="flex h-6 w-6 items-center justify-center rounded text-ink-4 transition hover:bg-canvas-2/60 hover:text-ink"
                  >
                    <Pencil size={11.5} />
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleDelete(tag)}
                    aria-label={`Delete ${tag.name}`}
                    className="flex h-6 w-6 items-center justify-center rounded text-ink-4 transition hover:bg-canvas-2/60 hover:text-rust"
                  >
                    <X size={13} />
                  </button>
                </div>
              </div>
            ),
          )}
        </div>
      )}
    </div>
  );
}
